import { assign, createMachine } from '@xstate/fsm'
import type { StateMachine } from '@xstate/fsm'
import type {
  DiscountCoupon,
  PaypayUnavailableCause,
  ReservationGivenPointsType,
  ReservationGivenPointsUsage,
  ReserveChildMenuInput,
  ReserveOptionMenuInput,
  ReservePaymentType,
  ReserveSuccessFragment,
} from '~/lib/graphql/graphql'
import type {
  DateText,
  DiscountCouponCode,
  HourMinute,
  PlanId,
  RestaurantId,
  SeatId,
} from '~/lib/graphql/scalars'
import type { CardBrand } from '~/lib/reservationInput/card'
import type { CardErrors, SelectedCardStatus } from '../lib/checkDisabled'
import {
  setAuthResult,
  toCardUpdatedContext,
  toGuestUpdatedContext,
  toMemberUpdatedContext,
  toNewReserveCondition,
  toNewUserStatus,
  toPreMemberUpdatedContext,
} from './assign'
import type {
  ReserveInputPlan,
  ReserveInputRestaurant,
  ReserveInputSeat,
  ReserveInputViewer,
} from './state'

export type ReserveInputStateMachine = StateMachine.State<
  ReservationInputContext,
  ReservationInputEvent,
  ReservationInputState
>

export type UserType = 'MEMBER' | 'GUEST' | 'PRE_MEMBER'

export type AuthResult = {
  key: string
  authCode: string
}

export type DisplaySeat = ReserveInputSeat & {
  label: string
  disabled: boolean
  childrenRestriction?: string
  chargeAmount: number
  chargeDetails: string
}

export type SelectedDiscountCouponType =
  | {
      type: 'AcquiredCoupon'
      code: DiscountCouponCode
    }
  | { type: 'InputtedCouponCode'; code?: DiscountCouponCode; errorMessage?: string }

export type SelectedCard =
  | {
      type: 'registered'
      cardId: string
    }
  | {
      type: 'new'
      newCard: NewCard
    }

export type NewCard = {
  maskedNumber: string
  holderName: string
  goodThru: string
  brand: CardBrand
  token: string
}

export type ReservationInputContext = {
  visitDate: DateText
  visitTime: HourMinute
  visitorsCount: number
  restaurantId: RestaurantId
  planId: PlanId
  seatId: SeatId | null
  userType?: UserType
  isTrusted?: boolean
  childCount: number
  reserverNameKana: string | null
  reserverPhoneNumber: string | null
  isReserverDifferentFromVisitor: boolean
  visitorNameKana: string | null
  visitorPhoneNumber: string | null
  email: string | null
  password: string | null
  /** ユーザーが能動的に選択した獲得ポイント利用方法 */
  givenPointsUsage: ReservationGivenPointsUsage | undefined
  givenPointsType: ReservationGivenPointsType
  ikyuPointUsage: number
  isIkyuPointEnabled: boolean
  isAllIkyuPointSelected: boolean
  paypayPointUsage: number
  isPayPayPointEnabled: boolean
  isAllPayPayPointSelected: boolean
  optionMenus: ReserveOptionMenuInput[] | null
  childMenus: ReserveChildMenuInput[] | null
  paymentMethod: ReservePaymentType
  card: SelectedCard | null
  discountCouponType: SelectedDiscountCouponType | null
  appliedDiscountCoupon: DiscountCoupon | null
  isDiscountCouponEnabled: boolean
  // ゲストやゲストで会員登録する場合は undefined
  // 会員登録して予約する際に false で予約するとすべてオプトアウトされてしまうため
  subscribeMailMagazineIkyuNews?: boolean
  subscribeMailMagazinePointCoupon?: boolean
  affiliateSiteFormAnswer: string | null
  surveyAnswer: string
  requestsAndComments: string
  signupOnly: boolean
  reservationResult: ReserveSuccessFragment | null
  authResult: AuthResult | null
  agreedToReserveRequest: boolean
}

export type ReservationInputEvent =
  | {
      type: 'init'
      visitDate: DateText
      visitTime: HourMinute
      visitorsCount: number
      restaurantId: RestaurantId
      planId: PlanId
      givenPointsUsage: 'SAVE'
      givenPointsType: ReservationGivenPointsType
      ikyuPointUsage: 0
      isIkyuPointEnabled: false
      isAllIkyuPointSelected: false
      paypayPointUsage: 0
      isPayPayPointEnabled: false
      isAllPayPayPointSelected: false
      childCount: 0
      isSameReserverAsVisitor: true
      agreedToReserveRequest: false
      affiliateSiteFormAnswer: ''
      surveyAnswer: ''
      isDiscountCouponEnabled: false
    }
  | { type: 'yahooMemberRelatingCompleted' }
  | {
      type: 'memberLoaded'
      seatId?: SeatId
      // 会員でユーザー情報が抜けている場合があるので optional
      reserverNameKana?: string
      reserverPhoneNumber?: string
      email?: string
      // カードを登録していない場合があるので optional
      card?: SelectedCard
      paymentMethod: ReservePaymentType
      // 獲得クーポンを取得していない場合があるので optional
      discountCouponType?: SelectedDiscountCouponType
      isDiscountCouponEnabled: boolean
      givenPointsType: ReservationGivenPointsType
      subscribeMailMagazineIkyuNews?: boolean
      subscribeMailMagazinePointCoupon?: boolean
    }
  | {
      type: 'guestLoaded'
      seatId?: SeatId
      // ゲストは必ず現地決済になる
      // 「会員登録する」チェックボタンを入れることでカード決済が可能になる
      paymentMethod: 'ONSITE'
      givenPointsType: ReservationGivenPointsType
    }
  | { type: 'trusted' }
  | { type: 'untrusted' }
  | { type: 'validated' }
  | {
      type: 'preMemberChanged'
      paymentMethod: ReservePaymentType
    }
  | { type: 'submitted' }
  | { type: 'authenticated'; result: AuthResult }
  | { type: 'closed' }
  | { type: 'loggedIn' }
  | { type: 'loginModalOpened'; signupOnly: boolean }
  | {
      type: 'reserveConditionChanged'
      visitorsCount: number
      visitDate: DateText
      visitTime: HourMinute
    }
  | { type: 'registered' }
  | { type: 'started' }
  | { type: 'succeeded'; result: ReserveSuccessFragment }
  | { type: 'failed' }
  | { type: 'recovered' }
  | { type: 'skipped' }
  | {
      type: 'registeredCard'
      card: SelectedCard
      userStatus: {
        card: {
          isInputCompleted: boolean
          errors: CardErrors
          selected: SelectedCardStatus | undefined
        }
      }
    }
  | UpdatedEvent

export type UpdatedEvent = {
  type: 'updated'
  seatId?: SeatId
  paymentMethod?: ReservePaymentType
  isDiscountCouponEnabled?: boolean
  discountCouponType?: SelectedDiscountCouponType
  // 適用したクーポンをリセットしたいので null を許容する
  appliedDiscountCoupon?: DiscountCoupon | null
  givenPointsUsage?: ReservationGivenPointsUsage
  givenPointsType?: ReservationGivenPointsType
  ikyuPointUsage?: number
  isIkyuPointEnabled?: boolean
  isAllIkyuPointSelected?: boolean
  paypayPointUsage?: number
  isPayPayPointEnabled?: boolean
  isAllPayPayPointSelected?: boolean
  reserverNameKana?: string
  reserverPhoneNumber?: string
  password?: string
  email?: string
  card?: SelectedCard
  isReserverDifferentFromVisitor?: boolean
  visitorNameKana?: string
  visitorPhoneNumber?: string
  childCount?: number
  childMenus?: ReserveChildMenuInput[]
  optionMenus?: ReserveOptionMenuInput[]
  affiliateSiteFormAnswer?: string
  requestsAndComments?: string
  subscribeMailMagazineIkyuNews?: boolean
  subscribeMailMagazinePointCoupon?: boolean
  agreedToReserveRequest?: boolean
  // assign で更新する前の validation で使用する値
  userStatus?: {
    restaurant?: ReserveInputRestaurant
    plan?: ReserveInputPlan
    seats?: DisplaySeat[]
    isMember?: boolean
    viewer?: ReserveInputViewer
    givenPoints?: number
    cause?: PaypayUnavailableCause
    availableMaxIkyuPoint?: number
    availableMaxPayPayBalance?: number
    childCount?: number
    card?: {
      isInputCompleted: boolean
      errors: CardErrors
      selected?: SelectedCardStatus
    }
  }
}

export type ReservationInputState =
  | { value: 'INITIAL'; context: ReservationInputContext }
  | { value: 'YAHOO_MEMBER_LINKING'; context: ReservationInputContext }
  | {
      value: 'RESERVE_INFO_LOADING'
      context: ReservationInputContext
    }
  | {
      value: 'UNTRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVABLE_UNTRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'CARD_REGISTRATION_UNTRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'AUTH_MODAL_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'TRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVABLE_TRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'CARD_REGISTRATION_TRUSTED_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'UNTRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVABLE_UNTRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'CARD_REGISTRATION_UNTRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'LOGIN_MODAL_UNTRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'AUTH_MODAL_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'TRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVABLE_TRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'CARD_REGISTRATION_TRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'LOGIN_MODAL_TRUSTED_GUEST'
      context: ReservationInputContext
    }
  | {
      value: 'PRE_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVABLE_PRE_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'CARD_REGISTRATION_PRE_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'LOGIN_MODAL_PRE_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'AUTH_MODAL_PRE_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'REGISTERING_MEMBER'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVE_READY'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVING'
      context: ReservationInputContext
    }
  | {
      value: 'RESERVE_SUCCESS'
      context: ReservationInputContext
    }

export const reservationInputMachine = createMachine<
  ReservationInputContext,
  ReservationInputEvent,
  ReservationInputState
>({
  /** @xstate-layout N4IgpgJg5mDOIC5QCc5mQNwIYBcCWA9gHYCSRADgK44B0JAciQCokCCAMgMR5F44DaABgC6iUOQKw+hImJAAPRAFoAzAEYATDRU6VAdj0BOABwqArABY1ANjMAaEAE9EtszUPWN1wxsEqNZoaCegC+IQ6osOjY+MRkVLQASgCiAMrJiQBqyQD6DABiAPI57IWsACIMAOKcALZgtQBG6OwEWBCQQqJIIBJSsbI9ighKaiqCNBZ6GnoW-t6aRtYOzgiWxjSCGl5mu1vWKsZqYRFomLgy8dQ0KelZuQXFpRXVnFCUcDit7Z0icn3SYhyYajDRqGg2CyCHw2NRGaHLJyITRmLT6QwqIKojQmMwnECRaIXOIUa4AVXoTESZNSTGS5RyAFlkoyAEIZTiUcgQXC-briSSAwagYb6LRqMzQwTWTQaYzWYwWFaIHRuMwKo5HdWCQR48IEs4xS6k2gUqk0ukM5lsjk4ZCUWA4Pn-QUDYGISzgww+PRmTE2Qx+wzKhDzGgBMxqPxgxUGY76wnnAZXU2U6m0+lMlnsxKcbAAGzwPKdEC6Lv6MndCD0mJo1iMU0EFimB2lIYs6vcpgs3Z7Mo0FnxiaNJISNzSGUyrFZ7FyZvTlqzNtzXOLzp6ALdQ0Qxml7gOhgstiMs2MGhD5jVfg7KJ8PfjpyiSeNY9uk+ns5y84tmetOc4doOiWZYbq6lbbggahqEe+46NYFhygYVjGBeXjhrsUb+GosahAmhrEqQJrjncU4znOaY-la2YcrAlCNLUfDAX8oEVkCEFjIGEJBOoNaWLs55IggHbWF2Kg9mJfaaIOeFPiOhFjqwZJMAAEkyhTlBwS7-lg1AABZgEQ+AAMa8qWzECqxwoKMiioiWMOq4tYgjGOq7adiYYm9vKUl6o+RLJkRikqWpGnsFpHJGfmkjrhZQpVtBwTuEY-jBN6HEXi57gqAqGLbFBwTGEO+EBWO5oZlRy6ctypkgbFW4iiq0wQpKQT9nKCpKoJ3rhrqgbrHoTl6MYuF+c+o7XGVi5-hyBZFjV5m9GBbENdWCoQn4Bxwk2w0GCGvhuDtA0Rjq3ZFbJBEpsR75kTkk2-tRK7VUx-KLZZVZxuGvqSn6Zi7l4wZdYYPWBLsPYDQVI0GudJXXG+WQfrkd0Vf+tH0YxMWvXF7FiUD0yHNBsKKmeIYJRCngeFMgZWNlZ3+S+5IUeVORVGSaRMFVa5mS9m7gStGj6No-OBuMTZbL6JPmHZuLU2J0wKrTY3yQzSPM6ztIAfajoYzzy3WaGOjhoYNjqnMfpTANJNQXZvVwnocJBgrcmXd+TMs2zeZYIWnO1Zj9V66iFibNMejQocA6GENJMzCJcw+jqmHSaNTtES7i5u+r5BYLAsAAO4EMgEAps95ZY3zgjgoctimMNmKHHMltnuG2W24GTm2InUN0+NSQTvDN2p5m6fs6u83c0tVnDMNQPDRYuLZai2UCasYzWCJtgzKYlhieqvmd4rl1w6Rn4DwyQ8a0B2vj-FWwTCYVOaLqu6olHmjuFsxjGIeHgSmYkPDhdRFD4Iy-IzNOat2aZ2znnAuRdL5vQgvBFQTdY77UPMNFCglNDjE2N4cSR4ZSHEdgA18vcj7kRVmfVGDEcDFxYqXPWmgJSbF-vzLwUIjYHCjr6dagRsrwV-tKawRCYa0CCqpRk6lNJnx0jgfShk8AmVoXVXmDDPBaCPN5YOzZnKImXjMNwUZeGrw7CHVewj6aiKUuIyRYUz6RWilzEufthj2T0OhAacYIafxfloIIcpy7YSNpoIRMku5K1oBQ8BHNR5OJUcMP04IBxHijLCXYFtMFRgmHbeC0E5j6HmOY7uNBInu0oIZTWSjfZxJVAOZqH8DDeBMDiFQIY76fTjF4eUNZDyFPCcUhcg8omzW9gtHWE9ECzCBlCAhm0AhTxJidTYXgvBihxOg3pl0SkZyzrnfOhcTSVLGVWYSNB1QDX8CHfQxjOqrCCEDX6Xh24eRyRswBpDgFbOHk9OB9Dhg+C0J6Ww4xVS6jUAsxudtOleDUBHCOu9-4iKun3T8nzOTlIvo4uhzjkTOTcENXqmIbzfUtjYHhRs4y+kxK8khJEPkDNPlEyBuyYEHJ+dihA8okHymbN6HQ0x2qtOyjQBpXhZiymCPC4qFikVkNuvS1W7sqHo0xco3WLiZiByhMNQRu5y5WAWeXbQIMPCfyadCalyt5UAAUUjhUeiMse8CVpwi9FBHEkZ-BYhciGVwNBP7QkVFCTyFrUwqxtbkaauZhkxKxdUtYthNi6hxCHOYcojwhgMG4cuzloRQXxYGENNAT45HDXazgTLoEQAAML5jAFgVAKqqlqp3GvIm8pPColXmMEMLkJhOUCI8-5u5CqhP3m82l-dQGZlLZG6Jhyr4QU-oHQFC8jpNh9d4cMwSP4P35rqQtQDJ1httbOiteya11obT7I5EFznCthE2KY8p1QYNWC5ESWwAgRxDoGZyI6k7ENhu8o91qT0PU4EqmhbK40onBLMXwhKjgDnghmo4dYkK+kPNCAakrobSrESFTSM7wMyLkcZGNqrxmQVXly8ugYJT4ubHoVD4J6yzB4jPXYhaCMSNCiWsDlV7FREbTe51WF3ADgklBD++CM1wjrJ-dQfiow9NHcnEhVQSAZkSNUMtqAoB4C1le0ZC7nUeDcJ4D+HgdCzzBLolwux9wzASliPJB7kiae07p2dAAzLAeBa0idMww7D61nJioQtMKYPrHOQtwbPDaph3MkVyCkCoABNCDOB63zqdXrbYm7DiWF1PBbi8ofVDQUxiANHCC1qcAz3EirxaJGSMmASA0Hm0ICFpqxUuLAjJRuR6HsSzRVymyksEJAHEWH1eH5gLnWqMDijILAJR5JSr29YJWYmrMReC2GMNhI79REAIB0eAPQEUWNiV1pQUw3C6AkuYBE0F7AZOFb1WeH9dxPssIWhgzA2DsBu1Ru7MxtC6GbM97+HYKsbByvtsE8wezJcnA8egRQShlEqPQKoIOqyjF3Kc1E0Fti+njq+lwlWEdOSR0dwtxbI344gkeCY2bDjSntmCNyIkPJ4MkgOXDYSD7AePlO5GGRmfOqbFoAa7DfQylhTz0S-PvKC+41YwjYUmexq6xKVemwjZnKNiY5XfOvL9g7ldopKsdeUeOXbHBQRy7eHGHCFpgl9p+oML6M8xs5i+lR8ixG8q7dNqo4YpBh44yok9J-ZjnvdTe5FXsZycEGfi4VbSKX+X6wQn0cawMMIo7Niyj4eCQaP6eCD7K4tQ8c+T26voMG9koKYkp5BfRZK+EmOlFbqVRSeM2Kz0wBvyJMRuPfr9X6-Lf4e70dwwxJhUpDsLZ8sfaw0LYW2GeDt3pdpdUVKc5Zv1xh5JR-VmbouQ+URHxvkwrqoRzDPAkjvmSK7GpymawwGfj0RoehvuNpMNKDoJoIGC5DWENtWJGKclGLuNlGlOqA+HvOpkBhOmLn-nahvt9l2PWN6FKLYH4BmtwtmruEEvmioBrsFLxkRgJjmBvhxPDuJDWDMH4NBB3kNKxkhBxrMOsO5p5nSDprjlgbrhHt0lushuYFCO6nDlVuNvHCmpQZftKofKlskBloAZkmTKvLsKVn4OVoJPWPDp-O6uwZYPBDXtUIAYStoLKNkjoFKFAUYXIXbDtvykodNioaQrkKkGSFWlWmkKkIAdqH6jfHKDCGCNMCGAIsKrZrCJhs2ELmOjSmjjkPkKwCQOwGSCkMEQbkLDvL6J0gYasJhnWFXHXMNLgkoWEEAA */
  id: 'reservationInput',
  initial: 'INITIAL',
  states: {
    INITIAL: {
      on: {
        init: {
          target:
            import.meta.env.VITE_MODE === 'ppg' ? 'YAHOO_MEMBER_LINKING' : 'RESERVE_INFO_LOADING',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              ...event,
            }
          }),
        },
      },
    },
    YAHOO_MEMBER_LINKING: {
      on: {
        yahooMemberRelatingCompleted: {
          target: 'RESERVE_INFO_LOADING',
        },
      },
    },
    RESERVE_INFO_LOADING: {
      // この state へ再度戻ってきたとき、 userType の derived atom を監視している atomEffect を発火させるため、userType を初期化している
      entry: assign((ctx) => toNewUserStatus(ctx)),
      on: {
        memberLoaded: {
          // PPG の場合は YAHOO_MEMBER_LINKING で実行しているLAFチェックで認証状態を確認済なので、常に TRUSTED_MEMBER へ遷移する
          // https://ikyu.atlassian.net/wiki/spaces/RESUIUX/pages/7015694341/PPG+SMS
          target: import.meta.env.VITE_MODE === 'ppg' ? 'TRUSTED_MEMBER' : 'UNTRUSTED_MEMBER',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              ...event,
            }
          }),
        },
        guestLoaded: {
          target: 'UNTRUSTED_GUEST',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              ...event,
            }
          }),
        },
      },
    },
    UNTRUSTED_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'MEMBER', false)),
      on: {
        updated: {
          actions: assign(toMemberUpdatedContext),
        },
        trusted: {
          target: 'TRUSTED_MEMBER',
        },
        validated: {
          target: 'RESERVABLE_UNTRUSTED_MEMBER',
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    RESERVABLE_UNTRUSTED_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'MEMBER', false)),
      on: {
        updated: {
          target: 'UNTRUSTED_MEMBER',
          actions: assign(toMemberUpdatedContext),
        },
        trusted: {
          target: 'RESERVABLE_TRUSTED_MEMBER',
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
        submitted: {
          target: 'CARD_REGISTRATION_UNTRUSTED_MEMBER',
        },
      },
    },
    CARD_REGISTRATION_UNTRUSTED_MEMBER: {
      on: {
        skipped: {
          target: 'AUTH_MODAL_MEMBER',
        },
        registeredCard: {
          target: 'AUTH_MODAL_MEMBER',
          actions: assign(toCardUpdatedContext),
        },
        failed: {
          target: 'UNTRUSTED_MEMBER',
        },
      },
    },
    AUTH_MODAL_MEMBER: {
      on: {
        authenticated: {
          target: 'RESERVE_READY',
          actions: assign(setAuthResult),
        },
        closed: {
          target: 'RESERVABLE_UNTRUSTED_MEMBER',
        },
      },
    },
    TRUSTED_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'MEMBER', true)),
      on: {
        updated: {
          actions: assign(toMemberUpdatedContext),
        },
        validated: {
          target: 'RESERVABLE_TRUSTED_MEMBER',
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    RESERVABLE_TRUSTED_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'MEMBER', true)),
      on: {
        updated: {
          target: 'TRUSTED_MEMBER',
          actions: assign(toMemberUpdatedContext),
        },
        submitted: {
          target: 'CARD_REGISTRATION_TRUSTED_MEMBER',
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    CARD_REGISTRATION_TRUSTED_MEMBER: {
      on: {
        skipped: {
          target: 'RESERVE_READY',
        },
        registeredCard: {
          target: 'RESERVE_READY',
          actions: assign(toCardUpdatedContext),
        },
        failed: {
          target: 'TRUSTED_MEMBER',
        },
      },
    },
    UNTRUSTED_GUEST: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'GUEST', false)),
      on: {
        updated: {
          actions: assign(toGuestUpdatedContext),
        },
        trusted: {
          target: 'TRUSTED_GUEST',
        },
        validated: {
          target: 'RESERVABLE_UNTRUSTED_GUEST',
        },
        preMemberChanged: {
          target: 'PRE_MEMBER',
          actions: assign(assignment),
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_UNTRUSTED_GUEST',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    RESERVABLE_UNTRUSTED_GUEST: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'GUEST', false)),
      on: {
        updated: {
          target: 'UNTRUSTED_GUEST',
          actions: assign(toGuestUpdatedContext),
        },
        trusted: {
          target: 'RESERVABLE_TRUSTED_GUEST',
        },
        preMemberChanged: {
          target: 'PRE_MEMBER',
          actions: assign(assignment),
        },
        submitted: {
          target: 'CARD_REGISTRATION_UNTRUSTED_GUEST',
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_UNTRUSTED_GUEST',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    CARD_REGISTRATION_UNTRUSTED_GUEST: {
      on: {
        skipped: {
          target: 'AUTH_MODAL_GUEST',
        },
        registeredCard: {
          target: 'AUTH_MODAL_GUEST',
          actions: assign(toCardUpdatedContext),
        },
        failed: {
          target: 'UNTRUSTED_GUEST',
        },
      },
    },
    LOGIN_MODAL_UNTRUSTED_GUEST: {
      on: {
        loggedIn: {
          target: 'RESERVE_INFO_LOADING',
        },
        closed: {
          target: 'UNTRUSTED_GUEST',
        },
      },
    },
    AUTH_MODAL_GUEST: {
      on: {
        authenticated: {
          target: 'RESERVE_READY',
          actions: assign(setAuthResult),
        },
        closed: {
          target: 'RESERVABLE_UNTRUSTED_GUEST',
        },
      },
    },
    TRUSTED_GUEST: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'GUEST', true)),
      on: {
        updated: {
          actions: assign(toGuestUpdatedContext),
        },
        untrusted: {
          target: 'UNTRUSTED_GUEST',
        },
        validated: {
          target: 'RESERVABLE_TRUSTED_GUEST',
        },
        preMemberChanged: {
          target: 'PRE_MEMBER',
          actions: assign(assignment),
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_TRUSTED_GUEST',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    RESERVABLE_TRUSTED_GUEST: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'GUEST', true)),
      on: {
        updated: {
          target: 'TRUSTED_GUEST',
          actions: assign(toGuestUpdatedContext),
        },
        untrusted: {
          target: 'RESERVABLE_UNTRUSTED_GUEST',
        },
        preMemberChanged: {
          target: 'PRE_MEMBER',
          actions: assign(assignment),
        },
        submitted: {
          target: 'CARD_REGISTRATION_TRUSTED_GUEST',
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_TRUSTED_GUEST',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    CARD_REGISTRATION_TRUSTED_GUEST: {
      on: {
        skipped: {
          target: 'RESERVE_READY',
        },
        registeredCard: {
          target: 'RESERVE_READY',
          actions: assign(toCardUpdatedContext),
        },
        failed: {
          target: 'TRUSTED_GUEST',
        },
      },
    },
    LOGIN_MODAL_TRUSTED_GUEST: {
      on: {
        loggedIn: {
          target: 'RESERVE_INFO_LOADING',
        },
        closed: {
          target: 'TRUSTED_GUEST',
        },
      },
    },
    PRE_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'PRE_MEMBER', false)),
      on: {
        updated: {
          actions: assign(toPreMemberUpdatedContext),
        },
        validated: {
          target: 'RESERVABLE_PRE_MEMBER',
        },
        preMemberChanged: {
          target: 'UNTRUSTED_GUEST',
          actions: assign(assignment),
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_PRE_MEMBER',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    RESERVABLE_PRE_MEMBER: {
      entry: assign((ctx) => toNewUserStatus(ctx, 'PRE_MEMBER', false)),
      on: {
        updated: {
          target: 'PRE_MEMBER',
          actions: assign(toPreMemberUpdatedContext),
        },
        preMemberChanged: {
          target: 'UNTRUSTED_GUEST',
          actions: assign(assignment),
        },
        submitted: {
          target: 'CARD_REGISTRATION_PRE_MEMBER',
        },
        loginModalOpened: {
          target: 'LOGIN_MODAL_PRE_MEMBER',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              signupOnly: event.signupOnly,
            }
          }),
        },
        reserveConditionChanged: {
          target: 'RESERVE_INFO_LOADING',
          actions: assign(toNewReserveCondition),
        },
      },
    },
    CARD_REGISTRATION_PRE_MEMBER: {
      on: {
        skipped: {
          target: 'AUTH_MODAL_PRE_MEMBER',
        },
        registeredCard: {
          target: 'AUTH_MODAL_PRE_MEMBER',
          actions: assign(toCardUpdatedContext),
        },
        failed: {
          target: 'PRE_MEMBER',
        },
      },
    },
    LOGIN_MODAL_PRE_MEMBER: {
      on: {
        loggedIn: {
          target: 'RESERVE_INFO_LOADING',
        },
        closed: {
          target: 'PRE_MEMBER',
        },
      },
    },
    AUTH_MODAL_PRE_MEMBER: {
      on: {
        authenticated: {
          target: 'REGISTERING_MEMBER',
          actions: assign(setAuthResult),
        },
        closed: {
          target: 'RESERVABLE_PRE_MEMBER',
        },
      },
    },
    REGISTERING_MEMBER: {
      on: {
        registered: {
          target: 'RESERVE_READY',
          actions: assign((ctx) => toNewUserStatus(ctx, 'MEMBER', true)),
        },
      },
    },
    RESERVE_READY: {
      on: {
        started: {
          target: 'RESERVING',
        },
      },
    },
    RESERVING: {
      on: {
        succeeded: {
          target: 'RESERVE_SUCCESS',
          actions: assign((ctx, event) => {
            return {
              ...ctx,
              reservationResult: event.result,
            }
          }),
        },
        // 予約失敗時のイベント
        // 予約失敗した際に navigation state でエラーふすまに遷移され
        // ブラウザバックすると予約入力ふすまに戻るが、前回の予約入力の state を保持するため
        // イベントを発火して初期状態に戻さないと RESERVING のままで何も入力できなくなってしまうので
        // RESERVE_INFO_LOADING に遷移させて initialize する
        recovered: {
          target: 'RESERVE_INFO_LOADING',
        },
      },
    },
    RESERVE_SUCCESS: {},
  },
})

function assignment<C, E extends { type: string }>(ctx: C, event: E): C {
  const { type, ...payload } = event
  return { ...ctx, ...payload }
}
